import { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn,
  stitchSectionLabel, stitchTooltipSlotProps,
} from '../ProjectOverview/parts/stitch';

// Waar een waarde vandaan komt, in mensentaal. AI-waarden zijn zichtbaar
// gemarkeerd tot iemand ze bevestigt — onbevestigd mag nooit ongezien in
// klantgerichte output (callsheet, offerte) belanden.
const BRON_LABELS = {
  briefing: 'uit klantdocument',
  meeting: 'uit transcript',
  afgeleid: 'AI',
  mens: 'handmatig',
};

/**
 * Het projectdossier: ~15 kernvelden met herkomst en bevestiging. Vervangt het
 * Airtable-briefingformulier (155 velden, nooit in gebruik genomen) — de AI
 * vult voor uit offerte/document/transcript, de PL bevestigt of corrigeert.
 *
 * `sectie` bepaalt welk deel deze fase toont: 'kern' (doel, doelgroep, …) of
 * 'productie' (locatie, meenemen/props — de iPad-test) — dat blok is pas
 * relevant zodra er draaidagen zijn.
 */
function DossierBlok({ projectRecId, projectNumber, sectie = 'kern', toonVast = false, standaardIngeklapt = false }) {
  const [data, setData] = useState(null); // { dossier, vast, veld_definities }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // { key, label, waarde }
  const [toonVerborgen, setToonVerborgen] = useState(false);
  // Ingeklapt met samenvattingsregel: vanaf preproductie (dan is het dossier
  // meestal al bevestigd) én zolang alles nog leeg is — leeg hoort stil te zijn.
  const [userToggle, setUserToggle] = useState(null);

  const laad = useCallback(async () => {
    if (!projectRecId) return;
    setLoading(true);
    try {
      const params = projectNumber ? `?projectNumber=${encodeURIComponent(projectNumber)}` : '';
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/dossier${params}`);
      setData(r.ok ? await r.json() : null);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [projectRecId, projectNumber]);
  useEffect(() => { laad(); }, [laad]);

  const patchVeld = async (key, body) => {
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/dossier/velden/${key}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setData((prev) => (prev ? { ...prev, dossier: d.dossier } : prev));
    } catch (e) { toast.error(`Dossier bijwerken mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const afleiden = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/dossier/afleiden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      if (d.melding) toast(d.melding, { icon: 'ℹ️' });
      else toast.success(`${d.afgeleid} veld${d.afgeleid === 1 ? '' : 'en'} ingevuld uit ${d.bronnen?.length || 0} bron${(d.bronnen?.length || 0) === 1 ? '' : 'nen'} — loop ze na en bevestig`);
      await laad();
    } catch (e) { toast.error(`Afleiden mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const bevestigOfferte = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/dossier/bevestig-offerte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectNumber }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      setData((prev) => (prev ? { ...prev, dossier: d.dossier, vast: d.vast } : prev));
      toast.success('Offertegegevens bevestigd — wijzigt Gripp ze later, dan zie je dat hier');
    } catch (e) { toast.error(`Bevestigen mislukt: ${e.message}`); }
    finally { setBusy(false); }
  };

  const alleDefinities = (data?.veld_definities || []).filter((v) => v.sectie === sectie);
  const velden = data?.dossier?.velden || {};
  // Weggeklikte velden zijn voor dít project niet relevant — ze tellen nergens
  // meer mee, maar blijven terug te halen via de toggle onderaan.
  const definities = alleDefinities.filter((d) => !velden[d.key]?.verborgen);
  const verborgenDefs = alleDefinities.filter((d) => velden[d.key]?.verborgen);
  const vast = data?.vast;
  const heeftWaarden = definities.some((d) => velden[d.key]?.waarde);

  const gevuld = definities.filter((d) => velden[d.key]?.waarde).length;
  const bevestigdAantal = definities.filter((d) => velden[d.key]?.bevestigd_op).length;
  const alleLeeg = !!data && gevuld === 0;
  const ingeklapt = userToggle !== null ? userToggle : (standaardIngeklapt || alleLeeg);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: ingeklapt ? 0 : 1, flexWrap: 'wrap' }}>
        <Box onClick={() => setUserToggle(!ingeklapt)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
          <Icon name={ingeklapt ? 'chevron_right' : 'expand_more'} size={17} sx={{ color: T.outlineVariant }} />
          <Typography sx={{ ...stitchSectionLabel }}>
            {sectie === 'productie' ? 'Dossier — voor de draaidag' : 'Projectdossier'}
          </Typography>
          {ingeklapt && data && (
            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, ml: 0.5 }}>
              {alleLeeg ? 'nog leeg' : `${gevuld}/${definities.length} ingevuld${bevestigdAantal ? ` · ${bevestigdAantal} bevestigd` : ''}`}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        {(!ingeklapt || alleLeeg) && (
          <Tooltip title="Vult lege velden uit briefing + meetings — bevestigde blijven staan" slotProps={stitchTooltipSlotProps}>
            <Box component="span" onClick={afleiden} sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem', opacity: busy ? 0.6 : 1 }}>
              {busy ? <CircularProgress size={14} /> : <Icon name="auto_awesome" size={15} />}
              {heeftWaarden ? 'Opnieuw afleiden' : 'Vullen met AI'}
            </Box>
          </Tooltip>
        )}
      </Box>

      {ingeklapt ? null : loading && !data ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: T.outline }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Dossier ophalen…</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {/* Vast-blok: live uit de Gripp-opdracht — niet gekopieerd, wél bevestigbaar.
              Leeg = één zachte regel, geen kader. */}
          {toonVast && !vast?.beschikbaar && (
            <Typography variant="caption" sx={{ gridColumn: '1 / -1', fontFamily: T.fontBody, color: T.outlineVariant, fontStyle: 'italic' }}>
              Nog geen offerteregels op de Gripp-opdracht.
            </Typography>
          )}
          {toonVast && vast?.beschikbaar && (
            <Box sx={{ gridColumn: '1 / -1', border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.6rem', p: 1.5,
              bgcolor: vast?.offerte_gewijzigd_sinds_bevestiging ? '#fff8e6' : 'transparent' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Icon name="request_quote" size={17} sx={{ color: T.tertiary }} />
                <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '0.85rem', color: T.text }}>
                  Uit de offerte
                </Typography>
                {vast?.beschikbaar ? (
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
                    {vast.count} regels · € {Number(vast.total_price || 0).toLocaleString('nl-NL')}
                    {vast.total_hours ? ` · ${vast.total_hours}u` : ''}
                    {vast.opleverdatum ? ` · oplevering ${String(vast.opleverdatum).slice(0, 10)}` : ''}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontStyle: 'italic' }}>
                    nog geen offerteregels op de Gripp-opdracht
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {vast?.beschikbaar && (
                  vast.offerte_gewijzigd_sinds_bevestiging ? (
                    <Tooltip title={`De offerte is in Gripp gewijzigd (${String(vast.gripp_updated_on || '').slice(0, 10)}) ná jouw bevestiging — kijk de regels na en bevestig opnieuw.`} slotProps={stitchTooltipSlotProps}>
                      <Box component="span" onClick={bevestigOfferte} sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem', color: '#9a6b00', borderColor: '#e0b64f' }}>
                        <Icon name="warning" size={15} /> Offerte gewijzigd — opnieuw bevestigen
                      </Box>
                    </Tooltip>
                  ) : data?.dossier?.offerte_bevestigd_op ? (
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <Icon name="check_circle" size={14} sx={{ color: '#3d8a53' }} />
                      bevestigd door {data.dossier.offerte_bevestigd_door || 'onbekend'}
                    </Typography>
                  ) : (
                    <Box component="span" onClick={bevestigOfferte} sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}>
                      <Icon name="check" size={15} /> Klopt
                    </Box>
                  )
                )}
              </Box>
              {vast?.beschikbaar && (
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, display: 'block', mt: 0.5 }}>
                  bron: Gripp-opdracht{vast.source === 'airtable' ? ' (via Airtable)' : ''} · stand {String(vast.synced_at || '').slice(0, 10) || 'onbekend'}
                </Typography>
              )}
            </Box>
          )}

          {definities.map((def) => {
            const veld = velden[def.key] || {};
            const bevestigd = !!veld.bevestigd_op;
            // Deliverables leven in hun eigen tabel (Airtable-master). Zodra die
            // gevuld is, is dát de bron — het dossierveld is alleen de
            // vroege-fase-wens uit de briefing.
            const liveDeliverables = def.key === 'deliverables' && (data?.deliverables_tabel?.count || 0) > 0
              ? data.deliverables_tabel : null;
            if (liveDeliverables) {
              return (
                <Box key={def.key} sx={{ border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.6rem', p: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontFamily: T.font, fontWeight: 800, color: T.secondary }}>
                      {def.label}
                    </Typography>
                    <Chip size="small" label="uit deliverables-tabel"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: T.tertiaryContainer + '55', color: T.tertiary }} />
                  </Box>
                  <Box sx={{ mt: 0.25 }}>
                    {liveDeliverables.items.map((it, i) => (
                      <Typography key={i} variant="caption" sx={{ fontFamily: T.fontBody, color: T.text, display: 'block' }}>
                        • {it.name}{it.deadline ? ` — ${it.deadline}` : ''}{it.status ? ` (${it.status})` : ''}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              );
            }
            return (
              <Box key={def.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start',
                border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.6rem', p: 1.25,
                // Rust: de bewerk/verberg-knopjes pas bij hover (op touch altijd zichtbaar)
                '&:hover .veldacties, &:focus-within .veldacties': { opacity: 1 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontFamily: T.font, fontWeight: 800, color: T.secondary }}>
                      {def.label}
                    </Typography>
                    {veld.waarde && veld.bron && (
                      <Tooltip title={veld.bron_ref ? `bron: ${veld.bron_ref}` : ''} slotProps={stitchTooltipSlotProps}>
                        <Chip size="small" label={BRON_LABELS[veld.bron] || veld.bron}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700,
                            bgcolor: veld.bron === 'mens' ? T.surfaceHigh : T.tertiaryContainer + '55',
                            color: veld.bron === 'mens' ? T.secondary : T.tertiary }} />
                      </Tooltip>
                    )}
                    {bevestigd && (
                      <Tooltip title={`Bevestigd door ${veld.bevestigd_door || 'onbekend'}`} slotProps={stitchTooltipSlotProps}>
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <Icon name="check_circle" size={14} sx={{ color: '#3d8a53' }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  {veld.waarde ? (
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.text, whiteSpace: 'pre-wrap', display: 'block', mt: 0.25 }}>
                      {veld.waarde}
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, fontStyle: 'italic', display: 'block', mt: 0.25 }}>
                      — niet bekend —
                    </Typography>
                  )}
                </Box>
                <Box className="veldacties" sx={{ display: 'flex', gap: 0.5, flexShrink: 0,
                  opacity: { xs: 1, md: 0 }, transition: 'opacity 0.12s ease' }}>
                  {veld.waarde && !bevestigd && (
                    <Tooltip title="Bevestig — dan mag het in callsheets/documenten" slotProps={stitchTooltipSlotProps}>
                      <Box component="span" onClick={() => !busy && patchVeld(def.key, { bevestig: true })}
                        sx={{ ...stitchOutlineBtn, py: 0.3, px: 0.9, fontSize: '0.72rem' }}>
                        <Icon name="check" size={14} />
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip title="Bewerken" slotProps={stitchTooltipSlotProps}>
                    <Box component="span" onClick={() => setEdit({ key: def.key, label: def.label, waarde: veld.waarde || '' })}
                      sx={{ ...stitchOutlineBtn, py: 0.3, px: 0.9, fontSize: '0.72rem' }}>
                      <Icon name="edit" size={14} />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Niet relevant — verbergen (terughalen kan onderaan)" slotProps={stitchTooltipSlotProps}>
                    <Box component="span" onClick={() => !busy && patchVeld(def.key, { verberg: true })}
                      sx={{ ...stitchOutlineBtn, py: 0.3, px: 0.9, fontSize: '0.72rem', color: T.outlineVariant }}>
                      <Icon name="visibility_off" size={14} />
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {!ingeklapt && verborgenDefs.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" onClick={() => setToonVerborgen((v) => !v)}
            sx={{ fontFamily: T.fontBody, color: T.outlineVariant, cursor: 'pointer', display: 'inline-block', '&:hover': { color: T.primary } }}>
            {toonVerborgen ? 'Verberg de weggeklikte velden' : `${verborgenDefs.length} veld${verborgenDefs.length === 1 ? '' : 'en'} weggeklikt voor dit project — tonen`}
          </Typography>
          {toonVerborgen && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
              {verborgenDefs.map((def) => (
                <Tooltip key={def.key} title="Klik om dit veld terug te halen" slotProps={stitchTooltipSlotProps}>
                  <Chip size="small" label={def.label} onClick={() => !busy && patchVeld(def.key, { verberg: false })}
                    sx={{ height: 22, fontSize: 11, fontWeight: 700, color: T.secondary, bgcolor: T.surfaceLow, cursor: 'pointer' }} />
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Veld bewerken */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>{edit?.label}</DialogTitle>
        <DialogContent>
          <textarea value={edit?.waarde || ''} onChange={(e) => setEdit((p) => ({ ...p, waarde: e.target.value }))} rows={4}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.6,
              border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, resize: 'vertical' }}
            placeholder="Kort en concreet — dit is wat het team (en straks de callsheet) leest" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box component="span" onClick={() => setEdit(null)} sx={stitchOutlineBtn}>Annuleren</Box>
          <Box component="span" sx={{ ...stitchGradientBtn, opacity: busy ? 0.6 : 1 }}
            onClick={async () => { if (!busy) { await patchVeld(edit.key, { waarde: edit.waarde }); setEdit(null); } }}>
            <Icon name="check" size={16} /> Bewaren
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DossierBlok;
